import * as React from 'react';
import { RadioSymbol } from 'ui';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { ComplexValue } from 'ui/common/types';
import { SelectSingle } from 'app/area/shared/translated-components';
import { LockedCase, UnlockCaseArgs } from 'app/area/case/features/unlock-case/types';
import { SharedState } from 'app/types-shared';
import { formatLockedDate } from 'app/area/case/features/unlock-case/format-locked-date';
import styles from './unlock-case-modal.scss';

export enum ChangeOptionType {
  DiscardChanges,
  ChoosePerson
}

export interface LockedByData {
  draftId: number;
  caseTitle: string;
  lockedBy: string;
  lockedDate: string;
}

export interface UnlockCaseModalProps {
  unlockCaseTranslate?: { [key: string]: string };
  caseId?: number;
  message?: string;
  locale: string;
  sharedState: SharedState;
  onCancel?: () => void;
  onContinue?: () => void;
  getLockedBy: (caseNumber: number) => LockedCase;
  getPersonOptions: (caseId: number, query?: string) => CodetableWireFormat[];
  discardUnlockCase: (args: Partial<UnlockCaseArgs>, translations: { [key: string]: string }) => void;
  choosePersonUnlockCase: (args: Partial<UnlockCaseArgs>, translations: { [key: string]: string }) => void;
}

export interface UnlockCaseModalState {
  options?: ComplexValue<number>[];
  person?: ComplexValue<number>;
  valid?: boolean;
  changeOptionType?: ChangeOptionType;
  lockedByData: LockedByData;
}

export class UnlockCaseModal extends React.Component<UnlockCaseModalProps, UnlockCaseModalState> {

  public constructor(props: UnlockCaseModalProps) {
    super(props);
    this.state = {
      changeOptionType: ChangeOptionType.DiscardChanges,
      lockedByData: undefined,
      valid: true
    };
  }

  public componentDidMount() {
    this.loadLockedByData();
  }

  private loadLockedByData = async () => {
    const lockedByData = await this.props.getLockedBy(this.props.caseId);

    this.setState({
      lockedByData: {
        draftId: lockedByData?.draftId,
        caseTitle: lockedByData?.caseTitle,
        lockedBy: lockedByData?.lockedBy?.d,
        lockedDate: lockedByData?.lockedDate
      }
    });
  };

  private loadOptions = async ({ query }: { query: string }) => {
    const users = await this.props.getPersonOptions(this.props.caseId, query);
    this.setState({ options: users.map(codeItemToStoreValue) });
  };

  private handleUserChanged = (person) => {
    this.setState({ person }, this.validate);
  };

  private handleSave = async () => {
    const { valid, person } = this.state;
    const { discardUnlockCase, choosePersonUnlockCase, onContinue, unlockCaseTranslate } = this.props;

    if (valid) {
      if (this.state.changeOptionType === ChangeOptionType.DiscardChanges) {
        discardUnlockCase && discardUnlockCase({
          draftId: this.state.lockedByData.draftId,
          personId: this.props.sharedState.id
        }, unlockCaseTranslate);
      } else {
        choosePersonUnlockCase && choosePersonUnlockCase({
          draftId: this.state.lockedByData.draftId,
          personId: person.value,
          personName: person.description
        }, unlockCaseTranslate);
      }

      onContinue && onContinue();
    }
  };

  private handleCancel = async () => {
    this.props.onCancel();
  };

  private validate = () => {
    const hasUserSelected = !!this.state.person?.value;

    this.setState({
      valid: this.state.changeOptionType !== ChangeOptionType.ChoosePerson || hasUserSelected
    });
  };

  private discardChanges = () => {
    this.setState({ changeOptionType: ChangeOptionType.DiscardChanges }, this.validate);
  };

  private choosePerson = () => {
    this.setState({ changeOptionType: ChangeOptionType.ChoosePerson }, this.validate);
  };

  public render() {
    const { caseId, unlockCaseTranslate, locale } = this.props;
    const { lockedByData, options, person, changeOptionType, valid } = this.state;

    const selectDiscardChanges = changeOptionType === ChangeOptionType.DiscardChanges;
    const selectChoosePerson = changeOptionType === ChangeOptionType.ChoosePerson;
    const formattedLockedDate = formatLockedDate(lockedByData?.lockedDate, locale);

    return (
      <ModalWrapper
        headerTitle={unlockCaseTranslate.unlock_case}
        btnTitle={unlockCaseTranslate.unlock_case_save}
        btnCancelTitle={unlockCaseTranslate.unlock_case_cancel}
        onClose={this.handleCancel}
        onDone={this.handleSave}
        isDisabled={!valid}
        closeOnDone={false}
        canCloseOnBackground={false}>
        <>
          <p className={styles.info}>
            {replaceTemplateString(unlockCaseTranslate.unlock_case_modal_title,
              caseId,
              lockedByData?.caseTitle,
              lockedByData?.lockedBy + '/' + formattedLockedDate)}
          </p>
          <div className="row">
            <div className="col-12">
              <div className={styles.container}>
                <div className="form-check">
                  <input
                    id="discard-changes"
                    type="radio"
                    className="form-check-input visuallyhidden"
                    value={ChangeOptionType.DiscardChanges}
                    checked={selectDiscardChanges}
                    onChange={this.discardChanges} />
                  <label
                    htmlFor="discard-changes"
                    className="form-check-label">
                    <RadioSymbol />
                    <b>{unlockCaseTranslate.unlock_case_modal_discard}</b>
                  </label>
                  {selectDiscardChanges
                    ? <p className={styles.textWrapper}>
                      {replaceTemplateString(unlockCaseTranslate.unlock_case_modal_discard_text,
                        lockedByData?.lockedBy)}
                    </p>
                    : null}
                  <input
                    id="choose-person"
                    type="radio"
                    className="form-check-input visuallyhidden"
                    value={ChangeOptionType.ChoosePerson}
                    checked={selectChoosePerson}
                    onChange={this.choosePerson} />
                  <label
                    htmlFor="choose-person"
                    className="form-check-label">
                    <RadioSymbol />
                    <b>{unlockCaseTranslate.unlock_case_modal_choose_person}</b>
                  </label>
                  {selectChoosePerson
                    ? <>
                      <p className={styles.textWrapper}>
                        {replaceTemplateString(unlockCaseTranslate.unlock_case_modal_choose_person_text,
                          lockedByData?.lockedBy)}
                      </p>
                      <div className={styles.dropdownWrapper}>
                        <SelectSingle
                          focus={true}
                          hideAlphabeticIndex={false}
                          className={'unlock-users-options'}
                          componentId={'unlock-users-options'}
                          options={options}
                          valueItem={person}
                          onLoadOptions={this.loadOptions}
                          onChange={this.handleUserChanged}
                          locale={locale} />
                      </div>
                    </>
                    : null}
                </div>
              </div>
            </div>
          </div>
        </>
      </ModalWrapper>
    );
  }
}
